import { PageMeta } from "../public-types";

export interface MemberData {
  member: MbMember;
}

export interface MbMember {
  id?: string; // 회원 관리 아이디
  memberNo?: string; // 회원 참조 번호
  email: string; // 이메일
  phoneNumber: string; // 휴대폰 번호
  deviceUniqueId?: string; // 디바이스 식별 정보
  roles: Array<MbMemberRole>; // 회원 권한 목록
  accountState?: MbMemberAccountState; // 회원 계정 상태
  states?: Array<MbMemberState>; // 회원 상태 목록
  organizations?: Array<MbMemberOrganization>; // 회원 소속 조직(회사) 목록
  lastModifiedDate: string; // 마지막 수정 일시
  nickname?: string; // 닉네임
}

// 회원 권한
export type MbMemberRole = "ROLE_STARTER" | "ROLE_MEMBER";

// 회원 계정 상태 타입
export type MbMemberAccountStatus = "AVAILABLE" | "LOCKED" | "INACTIVE" | "DELETED";

// 회원 계정 상태 변경 사유
export type MbMemberAccountCause = "JOIN" | "LOSS_PHONE_NUMBER_VERIFIED" | "RESTRICTION" | "RESOLVED" | "PRIVACY_EXPIRED" | "QUIT" | "CHANGE_DEVICE";

// 회원 상태 타입
export type MbMemberStateStatus =
  | "REQUIRE_JOIN"
  | "ALREADY_EXISTS_PHONE_NUMBER"
  | "JOINED"
  | "EMAIL_VERIFIED"
  | "PHONE_NUMBER_VERIFIED"
  | "MARKETING_CONSENT"
  | "MARKETING_CONSENT_DISAGREE"
  | "WAITING_FOR_RECOVERY";

// 회원 계정 상태
export interface MbMemberAccountState {
  id: string; // 계정 상태 정보 아이디
  status: MbMemberAccountStatus; // 계정 상태 타입
  statusDate: string; // 계정 상태 변경 일시
  cause: MbMemberAccountCause; // 계정 상태 변경 사유
  memberId: string; // 회원 관리 아이디
  restrictionId?: string; // 이용 중지 아이디
  login?: MbMemberLogin; // 로그인 내역
}

// 회원 상태
export interface MbMemberState {
  status: MbMemberStateStatus; // 회원 상태 타입
  statusDate: string; // 회원 상태 적용 일시
}

export interface MbMemberLogin {
  id?: string;
  memberId?: string;
  authTime?: string;
  client?: Client;
}

export interface MbMemberOrganization {
  id: string; // 조직(회사) 아이디
  name: string; // 회사 이름
}

export interface Client {
  userAgent: UserAgent;
  os: Os;
  device: Device;
}

export interface UserAgent {
  family?: string;
  major?: string;
  minor?: string;
  patch?: string;
}

export interface Os {
  family?: string;
  major?: string;
  minor?: string;
  patch?: string;
  patchMinor?: string;
}

export interface Device {
  family?: string;
}

// 회원 수정 response data
export interface MemberUpdateResponseData {
  member?: MbMember;
}

// SMS 인증 링크 발송 response data
export interface SmsVerificationResponseData {
  smsVerification?: {
    state: string; // SMS 인증 처리 문맥 상태 식별 정보
    expiresAt: string; // SMS 인증 코드 만료 일시
  };
}

// 이메일 인증 링크 발송 response data
export interface EmailVerificationResponseData {
  emailVerification?: {
    email: string; // 인증 처리 대상 이메일 주소
    expiresAt: string; // 이메일 인증 링크 만료 일시
  };
}

// SMS 인증 코드 검증 response data
export interface SmsCodeVerificationResponseData {
  smsCodeVerification?: {
    verified: boolean; // SMS 인증 코드 검증 여부
    errorCode?: "NEVER_SENT_SMS" | "INVALID_PHONE_NUMBER" | "INVALID_CURRENT_USER" | "INVALID_SMS_CODE" | "EXPIRED" | null; // SMS 인증 코드 검증 에러 코드
  };
}

export interface ContractAccessLog {
  id: string; // 아이디
  phoneNumber?: string; // 휴대폰 번호
  email?: string; // 이메일
  nickname?: string; // 닉네임
  userId?: string; // 회원번호
  deviceName?: string; // 디바이스 이름
  deviceId?: string; // 디바이스 아이디
  eventName?: string; // 이벤트 이름
  eventCode?: string; // 이벤트 코드
  eventGroup?: string; // 이벤트 그룹
  accessGroupIds?: string[]; // 출입그룹 아이디 목록
  userType?: "MEMBER" | "VISITOR"; // 유저 타입 (회원, 방문자)
  accessType?: ContractAccessLogAccessType; // 출입 타입
  datetime?: string; // 이벤트 발생 일시
}

// 출입 타입
export type ContractAccessLogAccessType =
  | "MEMBER_AUTHENTICATION" // 회원 인증
  | "MEMBER_MUTABLE" // 회원
  | "VISITOR_MUTABLE" // 방문자
  | "MEMBER_FIXED" // 회원 고정CSN
  | "MEMBER_FIXED_FACE" // 회원 얼굴
  | "KAKAO_QR" // 카카오QR
  | "VISITOR_FIXED" // 방문자 고정CSN
  | "MEMBER_MOBILE"; // 회원 모바일 출입증

// TAC22. 계약에 대한 출입로그 조회 (page token) 요청 파라미터
export interface ContractAccessLogPageTokenParams {
  contractManageId: number; // 계약관리 ID
  startDate: string; // 검색 시작일시
  endDate: string; // 검색 종료일시
  pageToken?: string; // 이전 응답의 nextPageToken (첫 페이지는 생략)
  pageSize?: number; // 페이지 사이즈 (default 20, max 400)
}

// TAC22. 출입로그 페이지네이션
export interface ContractAccessLogPagination {
  totalSize?: string; // 전체 항목 수 (int64라 문자열)
  pageSize: number; // 적용된 페이지 사이즈
  nextPageToken?: string | null; // 다음 페이지 토큰 (마지막 페이지면 null)
  orderBy: string; // 적용된 정렬 기준
}

// TAC22. 계약에 대한 출입로그 조회 (page token) 응답
export interface ContractAccessLogPageTokenResponse {
  logs: ContractAccessLog[];
  pagination: ContractAccessLogPagination;
}

// 동의 여부
export enum ConsentStatusCode {
  GRANTED = "GRANTED", // 동의
  DENIED = "DENIED", // 거부
}

// 사용앱 코드
export enum ServiceAppCode {
  TAAP = "TAAP", // Taap
  STPM = "STPM", // Smart Things Pro Mobile
  IFC = "IFC", // IFC Seoul
}

// 동의 리비전 정보
export interface ConsentAgreementRevision {
  agreementCode: string; // 동의 코드
  revision: number; // 리비전 번호
  title?: string; // 동의 제목
}

// 동의 항목
export interface ConsentAgreement {
  code: string; // 동의 코드
  activeRevision?: ConsentAgreementRevision; // 활성 리비전
}

// 동의 주체 식별 정보 (memberNo, contractVisitorId 중 하나)
export interface ConsentSubjectKey {
  memberNo?: string; // 회원번호
  contractVisitorId?: string; // 방문자 아이디
}

// 나의 동의 정보 (레코드)
export interface ConsentRecord {
  id: string; // 동의 레코드 아이디
  subjectKey: ConsentSubjectKey; // 동의 주체 식별 정보
  revision: ConsentAgreementRevision; // 동의 리비전 정보
  statusCode: ConsentStatusCode; // 동의 여부
  serviceAppCode: ServiceAppCode; // 사용앱 코드
  createdAt: string; // 동의 생성 시간
}

// 동의 목록 페이지네이션
export interface ConsentPagination {
  nextPageToken?: string; // 다음 페이지 토큰
  pageSize: number; // 적용된 페이지 사이즈
  orderBy: string; // 적용된 정렬 정보
}

// 나의 동의 목록 조회 요청 파라미터
export interface MemberConsentAgreementsRequestParam {
  agreementCode?: string; // 동의 대상 코드
  isUngrantedOnly?: boolean; // 동의하지 않은 문서 조회 (default: true)
  pageSize?: number; // 페이지 사이즈 (default: 100, maximum: 200)
  pageToken?: string; // 다음 페이지 토큰
}

// 나의 동의 목록 조회 응답
export interface MemberConsentAgreementsResponse {
  agreements: Array<ConsentAgreement>;
  pagination: ConsentPagination;
}

// 나의 동의 생성 요청
export interface MemberConsentAgreementCreateRequest {
  statusCode: ConsentStatusCode;
}

// 나의 동의 생성 응답
export interface MemberConsentAgreementCreateResponse {
  record: ConsentRecord;
}

// 이메일 중복 확인 응답
export interface EmailExistsInfo {
  email: string;
  exists: boolean;
}
